import * as THREE from 'three';
import { brushAabb, type Aabb, type Brush } from '../physics/Brush';
import type { FacilityRoom } from '../world/FacilityDressing';
import { listPortalWaypoints } from '../world/RoomCuller';

/** Per-enemy follow state — no heap allocs while walking a cached path. */
export interface NavFollow {
  path: THREE.Vector3[];
  i: number;
  goal: THREE.Vector3;
  repathT: number;
  stuck: THREE.Vector3;
  stuckT: number;
}

export function createNavFollow(): NavFollow {
  return {
    path: [],
    i: 0,
    goal: new THREE.Vector3(),
    repathT: 0,
    stuck: new THREE.Vector3(),
    stuckT: 0,
  };
}

export function clearNavFollow(nav: NavFollow): void {
  nav.path.length = 0;
  nav.i = 0;
  nav.repathT = 0;
  nav.stuckT = 0;
}

const PAD = 0.42;
const SAME_Y = 0.62;
const MAX_LINK = 22;
const DIRECT_DIST = 14;
const ARRIVE = 0.52;
const WAYPOINT_R = 0.58;
const GOAL_MOVE = 1.45;
const MAX_SEARCH = 3;
const GRID = 4.4;
const MERGE = 0.55;
const STUCK_TIME = 0.75;
const STUCK_MOVE = 0.18;

interface NavNode {
  x: number;
  y: number;
  z: number;
  nbs: number[];
  cost: number[];
}

interface Block {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

const _aabb: Aabb = { min: new THREE.Vector3(), max: new THREE.Vector3() };

/**
 * Sparse waypoint graph (room grid + doorways + obstacle rings) + A*.
 * Built once. At most three searches per frame; same-floor clear shots skip A*.
 */
export class FacilityNav {
  private nodes: NavNode[] = [];
  private blocks: Block[] = [];
  private searches = 0;
  private readonly cache = new Map<number, number[] | null>();
  private readonly g: number[] = [];
  private readonly came: number[] = [];
  private closedBuf = new Uint8Array(64);
  private readonly open: number[] = [];

  beginFrame(): void {
    this.searches = 0;
  }

  build(rooms: FacilityRoom[], brushes: Brush[]): void {
    this.nodes = [];
    this.blocks = [];
    this.cache.clear();

    for (const b of brushes) {
      if (!isNavBlocker(b)) continue;
      brushAabb(b, _aabb);
      this.blocks.push({
        minX: _aabb.min.x - PAD,
        maxX: _aabb.max.x + PAD,
        minY: _aabb.min.y,
        maxY: _aabb.max.y,
        minZ: _aabb.min.z - PAD,
        maxZ: _aabb.max.z + PAD,
      });
    }

    const add = (x: number, y: number, z: number): void => {
      if (this.nodeBlocked(x, y, z)) return;
      for (const n of this.nodes) {
        if (Math.abs(n.y - y) < 0.4 && Math.hypot(n.x - x, n.z - z) < MERGE) return;
      }
      this.nodes.push({ x, y, z, nbs: [], cost: [] });
    };

    for (const r of rooms) {
      add((r.x0 + r.x1) * 0.5, r.floorY, (r.z0 + r.z1) * 0.5);
      if (r.sparse) continue;
      const x0 = r.x0 + 1.5;
      const z0 = r.z0 + 1.5;
      const x1 = r.x1 - 1.5;
      const z1 = r.z1 - 1.5;
      if (x1 <= x0 || z1 <= z0) continue;
      for (let x = x0; x <= x1 + 0.05; x += GRID) {
        for (let z = z0; z <= z1 + 0.05; z += GRID) {
          add(x, r.floorY, z);
        }
      }
    }

    for (const p of listPortalWaypoints(rooms)) {
      add(p.x, p.y, p.z);
      for (const r of rooms) {
        if (Math.abs(r.floorY - p.y) > 0.65) continue;
        if (p.x < r.x0 - 0.8 || p.x > r.x1 + 0.8 || p.z < r.z0 - 0.8 || p.z > r.z1 + 0.8) continue;
        const cx = (r.x0 + r.x1) * 0.5;
        const cz = (r.z0 + r.z1) * 0.5;
        const dx = cx - p.x;
        const dz = cz - p.z;
        const len = Math.hypot(dx, dz);
        if (len < 0.2) continue;
        add(p.x + (dx / len) * 0.5, p.y, p.z + (dz / len) * 0.5);
      }
    }

    for (const b of brushes) {
      if (b.noCollision) continue;
      if (isWalkSlab(b.id)) {
        add(b.center.x, b.center.y + b.size.y * 0.5, b.center.z);
      }
      if (b.kind === 'ramp') {
        const c = Math.cos(b.yaw);
        const s = Math.sin(b.yaw);
        const hx = b.size.x * 0.5;
        const hy = b.size.y * 0.5;
        add(b.center.x + c * hx, b.center.y + hy, b.center.z - s * hx);
        add(b.center.x - c * hx, b.center.y - hy, b.center.z + s * hx);
      }
      if (!b.noCollision && isNavIsland(b.id)) {
        const fy = b.center.y - b.size.y * 0.5;
        const rad = Math.max(b.size.x, b.size.z) * 0.5 + 0.9;
        add(b.center.x + rad, fy, b.center.z);
        add(b.center.x - rad, fy, b.center.z);
        add(b.center.x, fy, b.center.z + rad);
        add(b.center.x, fy, b.center.z - rad);
      }
    }

    const n = this.nodes.length;
    const link = (i: number, j: number, cost: number): void => {
      const a = this.nodes[i];
      const b = this.nodes[j];
      if (a.nbs.includes(j)) return;
      a.nbs.push(j);
      a.cost.push(cost);
      b.nbs.push(i);
      b.cost.push(cost);
    };

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = this.nodes[i];
        const b = this.nodes[j];
        const xz = Math.hypot(a.x - b.x, a.z - b.z);
        if (xz < 0.12 || xz > MAX_LINK) continue;
        if (Math.abs(a.y - b.y) > SAME_Y) continue;
        if (!this.walkClear(a.x, a.y, a.z, b.x, b.y, b.z)) continue;
        link(i, j, xz + Math.abs(a.y - b.y) * 0.35);
      }
    }

    for (const b of brushes) {
      if (b.kind !== 'ramp') continue;
      const c = Math.cos(b.yaw);
      const s = Math.sin(b.yaw);
      const hx = b.size.x * 0.5;
      const hy = b.size.y * 0.5;
      const hi = this.nearest(b.center.x + c * hx, b.center.y + hy, b.center.z - s * hx);
      const lo = this.nearest(b.center.x - c * hx, b.center.y - hy, b.center.z + s * hx);
      if (hi < 0 || lo < 0 || hi === lo) continue;
      const a = this.nodes[hi];
      const d = this.nodes[lo];
      if (Math.hypot(a.x - (b.center.x + c * hx), a.z - (b.center.z - s * hx)) > 0.9) continue;
      if (Math.hypot(d.x - (b.center.x - c * hx), d.z - (b.center.z + s * hx)) > 0.9) continue;
      link(hi, lo, Math.hypot(a.x - d.x, a.z - d.z) + Math.abs(a.y - d.y) * 0.35);
    }

    if (this.closedBuf.length < n) this.closedBuf = new Uint8Array(n);
    this.g.length = n;
    this.came.length = n;
  }

  /**
   * Writes the next walk point. Returns false when already at the goal
   * (or the graph is empty — caller should idle / go direct).
   */
  nextHop(from: THREE.Vector3, goal: THREE.Vector3, follow: NavFollow, dt: number, out: THREE.Vector3): boolean {
    const gx = goal.x - from.x;
    const gz = goal.z - from.z;
    const gdist = Math.hypot(gx, gz);
    if (gdist < ARRIVE && Math.abs(goal.y - from.y) < 1.85) {
      follow.path.length = 0;
      return false;
    }

    follow.repathT = Math.max(0, follow.repathT - dt);
    const goalMoved = follow.goal.distanceToSquared(goal) > GOAL_MOVE * GOAL_MOVE;
    if (goalMoved) follow.goal.copy(goal);

    const dyGoal = Math.abs(goal.y - from.y);
    if (gdist < DIRECT_DIST && dyGoal < SAME_Y && this.walkClear(from.x, from.y, from.z, goal.x, goal.y, goal.z)) {
      follow.path.length = 0;
      out.copy(goal);
      this.trackStuck(from, follow, dt);
      return true;
    }

    if (goalMoved || follow.path.length === 0 || follow.repathT <= 0) {
      this.tryRepath(from, goal, follow);
    }

    if (follow.path.length === 0) {
      out.copy(goal);
      this.trackStuck(from, follow, dt);
      return true;
    }

    while (follow.i + 1 < follow.path.length) {
      const nxt = follow.path[follow.i + 1];
      if (Math.abs(nxt.y - from.y) > SAME_Y) break;
      if (!this.walkClear(from.x, from.y, from.z, nxt.x, nxt.y, nxt.z)) break;
      follow.i += 1;
    }

    let wp = follow.path[Math.min(follow.i, follow.path.length - 1)];
    if (Math.hypot(wp.x - from.x, wp.z - from.z) < WAYPOINT_R) {
      follow.i += 1;
      if (follow.i >= follow.path.length) {
        follow.path.length = 0;
        if (gdist < ARRIVE) return false;
        out.copy(goal);
        return true;
      }
      wp = follow.path[follow.i];
    }

    out.copy(wp);
    this.trackStuck(from, follow, dt);
    return true;
  }

  /** Same-floor capsule walk with no blocker on the segment. */
  hasWalk(from: THREE.Vector3, to: THREE.Vector3): boolean {
    if (Math.abs(to.y - from.y) > SAME_Y) return false;
    return this.walkClear(from.x, from.y, from.z, to.x, to.y, to.z);
  }

  /** Farthest-from-threat waypoint — civilians run here instead of into a wall. */
  pickFlee(from: THREE.Vector3, threat: THREE.Vector3, out: THREE.Vector3): void {
    let best = -Infinity;
    for (const n of this.nodes) {
      const away = Math.hypot(n.x - threat.x, n.z - threat.z);
      if (away < 3.5) continue;
      const near = Math.hypot(n.x - from.x, n.z - from.z);
      const score = away - near * 0.28 - Math.abs(n.y - from.y) * 0.8;
      if (score > best) {
        best = score;
        out.set(n.x, n.y, n.z);
      }
    }
    if (best === -Infinity) {
      const dx = from.x - threat.x;
      const dz = from.z - threat.z;
      const d = Math.hypot(dx, dz) || 1;
      out.set(from.x + (dx / d) * 8, from.y, from.z + (dz / d) * 8);
    }
  }

  private tryRepath(from: THREE.Vector3, goal: THREE.Vector3, follow: NavFollow): void {
    follow.repathT = 0.45 + Math.random() * 0.25;
    follow.goal.copy(goal);
    if (this.nodes.length === 0) {
      follow.path.length = 0;
      return;
    }
    if (this.searches >= MAX_SEARCH && follow.path.length > 0) return;
    this.searches += 1;

    const si = this.nearest(from.x, from.y, from.z);
    const gi = this.nearest(goal.x, goal.y, goal.z);
    if (si < 0 || gi < 0) {
      follow.path.length = 0;
      return;
    }

    const key = (si << 16) | gi;
    let idx = this.cache.get(key);
    if (idx === undefined) {
      idx = this.astar(si, gi);
      this.cache.set(key, idx);
    }
    follow.path.length = 0;
    follow.i = 0;
    if (!idx) return;
    for (const i of idx) {
      const n = this.nodes[i];
      follow.path.push(new THREE.Vector3(n.x, n.y, n.z));
    }
    follow.path.push(goal.clone());
    if (follow.path.length > 1) {
      const first = follow.path[0];
      if (Math.hypot(first.x - from.x, first.z - from.z) < WAYPOINT_R) follow.i = 1;
    }
  }

  private astar(start: number, goal: number): number[] | null {
    const n = this.nodes.length;
    if (start === goal) return [start];
    const g = this.g;
    const came = this.came;
    const closed = this.closedBuf;
    closed.fill(0, 0, n);
    for (let i = 0; i < n; i++) g[i] = Infinity;
    g[start] = 0;
    const open = this.open;
    open.length = 0;
    open.push(start);

    while (open.length) {
      let bi = 0;
      let best = Infinity;
      for (let o = 0; o < open.length; o++) {
        const i = open[o];
        const f = g[i] + this.h(i, goal);
        if (f < best) {
          best = f;
          bi = o;
        }
      }
      const cur = open[bi];
      open[bi] = open[open.length - 1];
      open.pop();
      if (cur === goal) return this.unwind(came, start, goal);
      if (closed[cur]) continue;
      closed[cur] = 1;
      const node = this.nodes[cur];
      for (let k = 0; k < node.nbs.length; k++) {
        const nb = node.nbs[k];
        if (closed[nb]) continue;
        const ng = g[cur] + node.cost[k];
        if (ng >= g[nb]) continue;
        g[nb] = ng;
        came[nb] = cur;
        open.push(nb);
      }
    }
    return null;
  }

  private unwind(came: number[], start: number, goal: number): number[] {
    const out = [goal];
    let cur = goal;
    let guard = this.nodes.length + 2;
    while (cur !== start && guard-- > 0) {
      cur = came[cur];
      out.push(cur);
    }
    out.reverse();
    return out;
  }

  private h(i: number, goal: number): number {
    const a = this.nodes[i];
    const b = this.nodes[goal];
    return Math.hypot(a.x - b.x, a.z - b.z) + Math.abs(a.y - b.y) * 0.35;
  }

  private nearest(x: number, y: number, z: number): number {
    let best = -1;
    let bestS = Infinity;
    for (let i = 0; i < this.nodes.length; i++) {
      const n = this.nodes[i];
      const s = Math.hypot(n.x - x, n.z - z) + Math.abs(n.y - y) * 1.6;
      if (s < bestS) {
        bestS = s;
        best = i;
      }
    }
    return best;
  }

  private walkClear(ax: number, ay: number, az: number, bx: number, by: number, bz: number): boolean {
    const y0 = Math.min(ay, by);
    const y1 = Math.max(ay, by) + 1.55;
    for (const b of this.blocks) {
      if (y1 < b.minY || y0 > b.maxY) continue;
      if (segHitsAabb2(ax, az, bx, bz, b.minX, b.maxX, b.minZ, b.maxZ)) return false;
    }
    return true;
  }

  private nodeBlocked(x: number, y: number, z: number): boolean {
    const y0 = y + 0.08;
    const y1 = y + 1.55;
    for (const b of this.blocks) {
      if (y1 < b.minY || y0 > b.maxY) continue;
      if (x >= b.minX && x <= b.maxX && z >= b.minZ && z <= b.maxZ) return true;
    }
    return false;
  }

  private trackStuck(from: THREE.Vector3, follow: NavFollow, dt: number): void {
    if (follow.stuckT <= 0) {
      follow.stuck.copy(from);
      follow.stuckT = 0.001;
      return;
    }
    if (Math.hypot(from.x - follow.stuck.x, from.z - follow.stuck.z) > STUCK_MOVE) {
      follow.stuck.copy(from);
      follow.stuckT = 0.001;
      return;
    }
    follow.stuckT += dt;
    if (follow.stuckT > STUCK_TIME) {
      follow.path.length = 0;
      follow.repathT = 0;
      follow.stuckT = 0;
      follow.i += 1;
    }
  }
}

function isNavIsland(id: string): boolean {
  return id.startsWith('pillar_') || id.startsWith('desk_') || id.startsWith('cabinet_') || id.startsWith('crate_');
}

function isWalkSlab(id: string): boolean {
  return (
    id.startsWith('floor_') ||
    id.startsWith('landing_') ||
    id.startsWith('lip_') ||
    id.startsWith('catwalk_')
  );
}

function isNavBlocker(b: Brush): boolean {
  if (b.noCollision || b.kind === 'ramp') return false;
  const id = b.id;
  if (isWalkSlab(id)) return false;
  if (id.startsWith('ceil_') || id.startsWith('step_') || id.startsWith('rail_')) return false;
  if (id.startsWith('safety') || id.startsWith('glass') || id.startsWith('hazard')) return false;
  if (id.startsWith('jamb_')) return false;
  return true;
}

function segHitsAabb2(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  minX: number,
  maxX: number,
  minZ: number,
  maxZ: number,
): boolean {
  let t0 = 0;
  let t1 = 1;
  const dx = bx - ax;
  const dz = bz - az;
  for (let i = 0; i < 2; i++) {
    const o = i === 0 ? ax : az;
    const d = i === 0 ? dx : dz;
    const mn = i === 0 ? minX : minZ;
    const mx = i === 0 ? maxX : maxZ;
    if (Math.abs(d) < 1e-12) {
      if (o < mn || o > mx) return false;
      continue;
    }
    let ta = (mn - o) / d;
    let tb = (mx - o) / d;
    if (ta > tb) {
      const tmp = ta;
      ta = tb;
      tb = tmp;
    }
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 > t1) return false;
  }
  return true;
}
